if (!customElements.get('starter-video')) {
  customElements.define('starter-video', class extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      if (!this.video) return;
      this.listeners = new AbortController();
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      const options = { signal: this.listeners.signal };
      this.video.addEventListener('pause', () => { if (this.shouldPlay) this.userPaused = true; }, options);
      this.video.addEventListener('play', () => { this.userPaused = false; }, options);
      this.reducedMotion.addEventListener('change', () => this.sync(), options);
      document.addEventListener('visibilitychange', () => this.sync(), options);
      this.observer = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        this.sync();
      }, { threshold: .2 });
      this.observer.observe(this);
    }

    sync() {
      this.shouldPlay = this.visible && !this.reducedMotion.matches && !document.hidden && !this.userPaused;
      if (this.shouldPlay) this.video.play().catch(() => {});
      else this.video.pause();
    }

    disconnectedCallback() {
      this.shouldPlay = false;
      this.video?.pause();
      this.observer?.disconnect();
      this.listeners?.abort();
    }
  });
}

if (!customElements.get('starter-workflow')) {
  customElements.define('starter-workflow', class extends HTMLElement {
    connectedCallback() {
      this.listeners = new AbortController();
      this.staticMode = matchMedia('(max-width: 760px), (prefers-reduced-motion: reduce)');
      this.video = this.querySelector('video');
      const options = { signal: this.listeners.signal };
      this.staticMode.addEventListener('change', () => this.sync(), options);
      document.addEventListener('visibilitychange', () => this.sync(), options);
      this.observer = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        this.sync();
      }, { threshold: 0.15 });
      this.observer.observe(this);
      this.sync();
    }

    disconnectedCallback() {
      this.stop();
      this.observer?.disconnect();
      this.listeners?.abort();
    }

    sync() {
      this.toggleAttribute('data-animated', !this.staticMode.matches);
      const shouldRun = this.visible && !this.staticMode.matches && !document.hidden;
      if (!shouldRun) this.stop();
      else if (!this.runController) {
        this.runController = new AbortController();
        this.animate(this.runController.signal).catch(error => {
          if (error.name !== 'AbortError') console.error(error);
        });
      }
    }

    stop() {
      this.runController?.abort();
      this.runController = null;
      cancelAnimationFrame(this.frame);
      this.video?.pause();
    }

    wait(ms, signal) {
      return new Promise((resolve, reject) => {
        const abort = () => { clearTimeout(timer); reject(new DOMException('Stopped', 'AbortError')); };
        const timer = setTimeout(() => { signal.removeEventListener('abort', abort); resolve(); }, ms);
        if (signal.aborted) abort();
        else signal.addEventListener('abort', abort, { once: true });
      });
    }

    async type(element, text, delay, signal) {
      for (let i = 0; i <= text.length; i++) {
        element.textContent = text.slice(0, i);
        await this.wait(delay, signal);
      }
    }

    chart(signal) {
      const canvas = this.querySelector('canvas');
      const context = canvas.getContext('2d');
      if (!context) return;
      const data = [], start = performance.now();
      const draw = () => {
        if (signal.aborted) return;
        const t = performance.now() - start, value = 22.6 + 1.9 * Math.sin(t * 0.00055) + 0.5 * Math.sin(t * 0.0021 + 1);
        data.push(value); if (data.length > 90) data.shift();
        const w = canvas.width, h = canvas.height, y = v => 10 + (h - 20) * (1 - (v - 18) / 10);
        context.clearRect(0, 0, w, h);
        context.strokeStyle = '#dedede'; context.lineWidth = 1;
        for (let i = 0; i <= 4; i++) { context.beginPath(); context.moveTo(0, 10 + (h - 20) * i / 4); context.lineTo(w, 10 + (h - 20) * i / 4); context.stroke(); }
        context.strokeStyle = '#171717'; context.lineWidth = 2.4; context.beginPath();
        data.forEach((v, i) => { if (i) context.lineTo(w * i / 89, y(v)); else context.moveTo(0, y(v)); });
        context.stroke();
        this.querySelector('.wf-temp').textContent = value.toFixed(1);
        this.querySelector('.wf-hum').textContent = Math.round(47 + 5 * Math.sin(t * 0.0004 + 2));
        this.frame = requestAnimationFrame(draw);
      };
      draw();
    }

    async animate(signal) {
      const find = selector => this.querySelector(selector);
      const stages = [...this.querySelectorAll('.s0, .s1, .s2, .s3')];
      const labels = ['Idea', 'Firmware', 'Modules', 'Upload'];
      const activate = index => {
        stages.forEach((stage, i) => stage.classList.toggle('active', i === index));
        const progress = `${(index + .5) / 4 * 100}%`;
        find('.wf-fill').style.width = progress; find('.wf-token').style.left = progress;
        find('.wf-steplabel').textContent = labels[index];
      };
      while (!signal.aborted) {
        cancelAnimationFrame(this.frame);
        find('.wf').classList.remove('revealed');
        find('.wf-devA').textContent = '03'; find('.wf-devB').textContent = 'modules';
        find('.wf-prompt-t').textContent = ''; find('.wf-code').textContent = '';
        find('.wf-bar-fill').style.width = '0'; find('.wf-flashed').classList.remove('show');
        activate(0);
        await this.type(find('.wf-prompt-t'), "Chart my room’s temperature & humidity live", 25, signal);
        await this.wait(500, signal);
        activate(1);
        await this.type(find('.wf-code'), 'dht.every(2000, r => {\n  dash.push("temp", r.tempC)\n  dash.push("rh", r.humidity)\n})', 12, signal);
        await this.wait(700, signal);
        activate(2);
        if (this.video) {
          this.video.currentTime = 0;
          await this.video.play().catch(() => {});
        }
        await this.wait(Math.min(15000, Math.max(2200, (this.video?.duration || 0) * 1000)), signal);
        this.video?.pause();
        activate(3);
        for (let progress = 0; progress <= 100; progress += 4) {
          find('.wf-bar-fill').style.width = `${progress}%`;
          await this.wait(20, signal);
        }
        find('.wf-flashed').classList.add('show');
        await this.wait(600, signal);
        stages.forEach(stage => stage.classList.remove('active'));
        find('.wf-devA').textContent = 'Working project'; find('.wf-devB').textContent = '';
        find('.wf-steplabel').textContent = 'Working project';
        find('.wf-fill').style.width = '100%'; find('.wf-token').style.left = '100%';
        find('.wf').classList.add('revealed');
        this.chart(signal);
        await this.wait(4000, signal);
      }
    }
  });
}
